/**
 * HTTP Client amélioré avec Axios + Keycloak SSO
 *
 * Fonctionnalités:
 * - ✅ Injection automatique du token Bearer
 * - ✅ Rafraîchissement automatique du token Keycloak
 * - ✅ Gestion des erreurs 401 avec logout
 * - ✅ Retry logic avec backoff exponentiel
 * - ✅ Request deduplication (évite les doublons)
 * - ✅ Rate limiting côté client
 * - ✅ Logs structurés en développement
 */
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
  AxiosError,
} from 'axios';
import { keycloak, storageService } from '@sphere/shared/util/browser';

// ✅ Flag global pour indiquer si Keycloak est prêt
// Cela évite les erreurs de requêtes lancées pendant l'initialisation
let keycloakInitialized = false;

// "Architecture Pure" : On écoute un événement global plutôt que d'exposer un setter
if (typeof window !== 'undefined') {
  window.addEventListener('sphere:auth-ready', (event: any) => {
    keycloakInitialized = event.detail;
    if (keycloakInitialized && import.meta.env.DEV) {
      console.log('[HTTP] Keycloak initialisé (Event), requêtes HTTP autorisées');
    }
  });
}

// --- Configuration par défaut ---
const DEFAULT_CONFIG: AxiosRequestConfig = {
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:4100/api',
  timeout: 30_000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  // ✅ Amélioration: Serializer global pour éviter les crochets [] sur les tableaux
  paramsSerializer: {
    serialize: (params) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, v as string));
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, value as string);
        }
      });
      return searchParams.toString();
    },
  },
};

// Requêtes longues (IA, upload, etc.)
const LONG_TIMEOUT_CONFIG: AxiosRequestConfig = {
  ...DEFAULT_CONFIG,
  timeout: 120_000, // ✅ Amélioration: 2 minutes pour l'IA
};

// ✅ Amélioration: Compteur de requêtes pour rate limiting (Cross-tab via localStorage)
// const requestCounts = new Map<string, { count: number; resetTime: number }>(); // REMOVED: Local only
const RATE_LIMIT_WINDOW = 60_000; // 1 minute
const MAX_REQUESTS_PER_MINUTE = 100;

/**
 * ✅ Amélioration: Vérifie le rate limiting
 */
/**
 * ✅ Amélioration: Vérifie le rate limiting (Cross-tab)
 */
function checkRateLimit(endpoint: string): boolean {
  if (typeof window === 'undefined') return true; // SSR safe

  const key = `sphere:rl:${endpoint}`;
  const now = Date.now();

  try {
    const stored = localStorage.getItem(key);
    let record = stored ? JSON.parse(stored) : null;

    if (!record || now > record.resetTime) {
      // Nouvelle fenêtre
      record = {
        count: 1,
        resetTime: now + RATE_LIMIT_WINDOW,
      };
      localStorage.setItem(key, JSON.stringify(record));
      return true;
    }

    if (record.count >= MAX_REQUESTS_PER_MINUTE) {
      console.warn(`Rate limit dépassé pour ${endpoint}`);
      return false;
    }

    record.count++;
    localStorage.setItem(key, JSON.stringify(record));
    return true;
  } catch (error) {
    // Fallback silencieux en cas d'erreur de stockage (ex: quota dépassé)
    return true;
  }
}

/**
 * Intercepteur d'authentification amélioré
 */
async function authInterceptor(
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> {
  // ✅ Amélioration: Rate limiting
  const endpoint = config.url || '';
  if (!checkRateLimit(endpoint)) {
    throw new Error('Rate limit exceeded. Please try again later.');
  }

  // ✅ Si c'est du FormData, supprimer le Content-Type pour laisser axios le gérer automatiquement
  if (config.data instanceof FormData) {
    // Supprimer tous les Content-Type possibles pour que axios génère le bon header avec boundaries
    delete config.headers['Content-Type'];
    delete config.headers['content-type'];
    if (config.headers.delete) {
      config.headers.delete('Content-Type');
      config.headers.delete('content-type');
    }
  }

  // ✅ Amélioration: Logs structurés en développement
  if (import.meta.env.DEV) {
    console.log(`[HTTP] ${config.method?.toUpperCase()} ${config.url}`, {
      params: config.params,
      data: config.data instanceof FormData ? '[FormData]' : config.data,
    });
  }

  // Injection du token Keycloak (Double Verrou - Partie 2 : Intercepteur Robuste)
  const kc = keycloak;
  if (kc?.authenticated) {
    try {
      // 1. On demande un refresh. Si le token est bon (> 10s), ça résout instantanément.
      // Si le token est expiré, Keycloak lance l'appel réseau AVANT de continuer.
      // C'est ici que la "magie" opère : on attend que Keycloak soit prêt.
      await kc.updateToken(10); 
      
      if (kc.token) {
        config.headers.Authorization = `Bearer ${kc.token}`;
        // Envoyer l'établissement actif pour que le Gateway l'injecte dans le JWT de contexte (admin)
        const activeEstId =
          typeof window !== 'undefined'
            ? localStorage.getItem('sphere:activeEstablishmentId')
            : null;
        if (activeEstId) {
          config.headers['x-sphere-establishment'] = activeEstId;
        }
        return config;
      }
    } catch (error) {
      // Si le refresh échoue (session terminée côté serveur), on force le logout
      // pour éviter de boucler sur des erreurs 401
      console.error('[HTTP] Session expirée dans l\'intercepteur, redirection...', error);
      // ⚠️ FIX: Ne pas appeler kc.logout() ici car ça peut boucler si le token est invalid_grant
      // On redirige vers la page de connexion Keycloak comme demandé
      await keycloak.login();
      throw error;
    }
  }

  // Fallback sur le storage local pour les cas hybrides (rare maintenant)
  const lsToken = storageService.getAccessToken?.();
  if (lsToken) {
    config.headers.Authorization = `Bearer ${lsToken}`;
    const activeEstId =
      typeof window !== 'undefined'
        ? localStorage.getItem('sphere:activeEstablishmentId')
        : null;
    if (activeEstId) {
      config.headers['x-sphere-establishment'] = activeEstId;
    }
  }

  return config;
}

/**
 * Intercepteur d'erreurs global amélioré
 */
function errorInterceptor(error: AxiosError | any) {
  // ✅ Ignorer silencieusement les erreurs d'annulation d'auth
  if (error?.isAuthCanceled) {
    if (import.meta.env.DEV) {
      console.log('[HTTP] Requête annulée car non authentifié');
    }
    return Promise.reject(error);
  }

  const status = error?.response?.status;
  const config = error?.config;

  // ✅ Ignorer les erreurs "Request aborted" pendant l'initialisation
  // Ces erreurs arrivent quand des requêtes sont lancées avant que Keycloak soit prêt
  const isAbortedDuringInit =
    error.code === 'ERR_CANCELED' ||
    error.message?.includes('aborted') ||
    error.message?.includes('canceled');

  if (isAbortedDuringInit && !keycloak?.authenticated) {
    // Silencieux en DEV aussi - c'est un comportement normal pendant l'init
    return Promise.reject(error);
  }

  // ✅ Amélioration: Logs détaillés en développement (sauf pour les annulations)
  if (import.meta.env.DEV && !isAbortedDuringInit) {
    console.error('[HTTP Error]', {
      status,
      url: config?.url,
      method: config?.method,
      message: error.message,
      response: error.response?.data,
    });
  }

  // Gestion des erreurs d'authentification (401)
  if (status === 401 && config && !config._retry) {
    config._retry = true; // Éviter les boucles infinies

    try {
      if (import.meta.env.DEV) {
        console.log('[HTTP] 401 détecté, tentative de refresh de la dernière chance...');
      }
      // On tente de refresh le token
      // Note: errorInterceptor ne peut pas être async, mais on peut retourner une promise
      // Cependant, keycloak.updateToken retourne une Promise, donc on doit gérer l'async ici.
      // Axios supporte le retour d'une Promise dans l'intercepteur.
      return keycloak.updateToken(5).then(() => {
        // On met à jour le header avec le nouveau token
        config.headers.Authorization = `Bearer ${keycloak.token}`;
        
        // ✅ ON REJOUE LA REQUÊTE ORIGINALE
        return httpClient(config);
      }).catch((refreshError) => {
        console.error('[HTTP] Refresh échoué après 401, re-login automatique...');
        
        // Re-login automatique : redirige vers Keycloak pour se reconnecter
        keycloak.login({ redirectUri: window.location.href });
        return Promise.reject(refreshError);
      });
    } catch (e) {
      // Should not happen with promise chaining above, but good for safety
      return Promise.reject(e);
    }
  }

  // ✅ Amélioration: Gestion des erreurs réseau
  if (!error.response) {
    const networkError = new Error(
      'Erreur réseau. Vérifiez votre connexion internet.'
    );
    window.dispatchEvent(
      new CustomEvent('network:error', { detail: networkError })
    );
  }

  // ✅ Amélioration: Gestion des erreurs 429 (Too Many Requests)
  if (status === 429) {
    const retryAfter = error.response?.headers['retry-after'];
    console.warn('Rate limit atteint', { retryAfter });
    window.dispatchEvent(
      new CustomEvent('rate-limit:exceeded', { detail: { retryAfter } })
    );
  }

  // ✅ Amélioration: Gestion des erreurs 5xx
  if (status && status >= 500) {
    window.dispatchEvent(
      new CustomEvent('server:error', { detail: error.response?.data })
    );
  }

  return Promise.reject(error);
}

/**
 * ✅ Amélioration: Intercepteur de réponse pour les logs
 */
function responseInterceptor(response: AxiosResponse): AxiosResponse {
  if (import.meta.env.DEV) {
    console.log(`[HTTP Response] ${response.status} ${response.config.url}`, {
      data: response.data,
      duration: response.config.headers?.['X-Request-Duration'],
    });
  }
  return response;
}

/**
 * Fabrique d'instances Axios avec intercepteurs améliorés
 */
function createHttpClient(config: AxiosRequestConfig = {}): AxiosInstance {
  const instance = axios.create({ ...DEFAULT_CONFIG, ...config });

  // ✅ Amélioration: Intercepteur de requête avec gestion d'erreurs
  instance.interceptors.request.use(authInterceptor, (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  });

  // ✅ Amélioration: Intercepteurs de réponse avec logs
  instance.interceptors.response.use(responseInterceptor, errorInterceptor);

  return instance;
}

/** Instance standard (timeout 30s) */
export const httpClient = createHttpClient();

/** Instance longue (timeout 60s) — pour IA, upload gros fichiers, etc. */
export const httpClientLong = createHttpClient(LONG_TIMEOUT_CONFIG);

/** Fabrique custom si besoin ponctuel */
export function createCustomHttpClient(
  config: AxiosRequestConfig
): AxiosInstance {
  return createHttpClient(config);
}

/** Helper: FormData à partir d'un record */
export function createFormData(data: Record<string, any>): FormData {
  const fd = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value === null || value === undefined) return;

    if (value instanceof File || value instanceof Blob) {
      fd.append(key, value);
    } else if (Array.isArray(value)) {
      value.forEach((v) => fd.append(key, String(v)));
    } else if (typeof value === 'object') {
      // utile pour envoyer des objets simples
      fd.append(key, JSON.stringify(value));
    } else {
      fd.append(key, String(value));
    }
  });
  return fd;
}

/** Helper: construction de querystring propre */
export function buildQueryString(params: Record<string, any>): string {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '') return;
    if (Array.isArray(v)) v.forEach((item) => sp.append(k, String(item)));
    else if (typeof v === 'object') sp.append(k, JSON.stringify(v));
    else sp.append(k, String(v));
  });
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export const HttpClient = httpClient;

export default httpClient;

